import { useQuery } from '@tanstack/react-query';
import { fetchSearchResults } from './useSearch';

// Cantidad mínima de caracteres antes de pedir sugerencias (evita consultas
// demasiado amplias) y tope de sugerencias a mostrar.
export const MIN_SUGGESTION_LENGTH = 3;
const MAX_SUGGESTIONS = 8;

interface UseProfessionalSuggestionsParams {
  plan: string;
  /** Texto tipeado (idealmente ya "debounced"). */
  text: string;
  /** Especialidad secundaria elegida, para acotar igual que la búsqueda final. */
  especialidadNombre?: string;
  enabled?: boolean;
}

/**
 * Sugerencias predictivas de nombres de profesionales.
 *
 * Reusa el mismo origen de datos que la búsqueda final (`/api/search` → WebAPI
 * de Medicus, que ya filtra por coincidencia parcial vía `vTexto`) y extrae
 * nombres únicos con formato "Apellido, Nombre" para mostrarlos mientras se
 * escribe. Requiere un plan elegido (la WebAPI lo exige).
 */
export function useProfessionalSuggestions({
  plan,
  text,
  especialidadNombre,
  enabled = true,
}: UseProfessionalSuggestionsParams) {
  const query = text.trim();

  return useQuery({
    queryKey: ['professional-suggestions', plan, query, especialidadNombre ?? ''],
    queryFn: async (): Promise<string[]> => {
      const results = await fetchSearchResults({
        plan,
        searchTypeCode: 'C1', // "Nombre del profesional"
        vTexto: query,
        especialidadNombre,
      });

      // Nombres únicos preservando el orden en que llegan. Cuando no hay nombre
      // (registros sin coma) usamos solo el apellido para no duplicar el texto.
      const seen = new Set<string>();
      const suggestions: string[] = [];
      for (const p of results.professionals) {
        const apellido = p.APELLIDO?.trim() || '';
        const nombre = p.NOMBRE?.trim() || '';
        const label = nombre && nombre !== apellido ? `${apellido}, ${nombre}` : apellido;
        const key = label.toLowerCase();
        if (label && !seen.has(key)) {
          seen.add(key);
          suggestions.push(label);
        }
        if (suggestions.length >= MAX_SUGGESTIONS) break;
      }
      return suggestions;
    },
    enabled: enabled && !!plan && query.length >= MIN_SUGGESTION_LENGTH,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}
